import { useContext, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { db } from '../../../firebase/config.js';
import useSharedCollectionWhere from '../../../firebase/useSharedCollectionWhere.js';
import { client } from '../../../hooks/Client.js';
import Location from '../../../hooks/Location.js';
import useSubtaskStatusChange from '../../../hooks/useSubtaskStatusChange.js';
import { Auth } from '../../../StateManagment/Auth.js';
import AddItemTableRow from '../../common/AddItemTableRow.jsx';
import ReadOnlyGuard from '../../common/ReadOnlyGuard';
import Table from '../../common/table/Table.jsx';
import TableCell from '../../common/table/TableCell.jsx';
import buildSubtaskDoc from '../../../helpers/adressHelpers/buildSubtaskDoc.js';
import subtasksPerAdress, { STATUS_LABELS, subtaskStatusLabel, isOpenSubtask } from '../../../helpers/adressHelpers/subtasksPerAdress.js';
import { APPOINTMENT_TYPE, SUBTASK_TYPE, isAppointment } from '../../../helpers/adressHelpers/taskKind.js';

/** Eén formatter, buiten de component: er is niets per render aan. */
const DATE_FORMATTER = new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });

/**
 * De datum van de afspraak waar een subtaak onder hangt.
 *
 * Een afspraak zonder datum bestaat, en dan hoort er een streepje te staan en
 * geen 'Invalid Date'.
 *
 * @param {*} timestamp - Firestore-Timestamp van de afspraak, of null
 * @returns {string}
 */
const formatParentDate = (timestamp) => {
    const datum = timestamp?.toDate?.();
    if (!(datum instanceof Date) || Number.isNaN(datum.getTime())) return '—';
    return DATE_FORMATTER.format(datum);
};

/**
 * Wat er in de kolom 'BIJ AFSPRAAK' komt te staan.
 *
 * Eerst `planned`, dán `parentFound` — in die volgorde. Een subtaak die nog
 * niet ingepland is heeft geen afspraak om te vinden; die zou anders lezen als
 * 'Afspraak verwijderd', en dat is een storing waar er geen is.
 *
 * Een ingeplande subtaak kan zijn afspraak overleven. Dan is een kaal streepje
 * verwarrend — dat leest als 'niet ingevuld' of als een storing, terwijl het
 * werk gewoon nog ligt en alleen de afspraak weg is.
 *
 * @param {{planned: boolean, parentFound: boolean, parentTitle: string}} item
 * @returns {string}
 */
const parentLabel = (item) => {
    if (!item.planned) return 'Nog niet ingepland';
    if (!item.parentFound) return 'Afspraak verwijderd';
    return item.parentTitle || '—';
};

/**
 * Alle subtaken van dit huishouden bij elkaar, over de afspraken heen — en
 * sinds 2026-09-30 ook de plek om er een aan te maken.
 *
 * WAAROM DIT VROEGER ALLEEN-LEZEN WAS, EN NU NIET MEER: een subtaak hoorde bij
 * een afspraak, en dáár stond hij. Twee schermen die hetzelfde document
 * schrijven lopen uiteen — dat was de reden. Die reden vervalt nu het
 * huishouden de eigenaar is: een subtaak bestaat ook zonder afspraak ('nog niet
 * ingepland'), en dan is dit tabblad de enige plek waar hij kán ontstaan. De
 * titel wordt hier vooraf gevraagd; de tekst van een bestaande subtaak pas je
 * nog steeds aan bij de afspraak. De stand wel hier, via dezelfde hook als de
 * afspraakbewerker, zodat de twee niet uiteen kunnen lopen.
 * AI-CONTEXT: See docs/features/subtaken-bij-het-huishouden.md
 *
 * Updateverzoeken BuurtKlusBedrijf: 'de subtaken van een huishouden terugzien
 * als eigen tabblad' (2026-09-15) en 'op de computer kunnen toevoegen, met een
 * plusje onder de tabel' (2026-09-28).
 *
 * @param {object} props
 * @param {string} props.adressNavigation - Welk tabblad open staat
 * @returns {JSX.Element}
 */
const HouseholdSubtasks = ({ adressNavigation }) => {
    const adressId = Location()[3];
    const [auth] = useContext(Auth);
    const { runStatusChange } = useSubtaskStatusChange();

    const [adding, setAdding] = useState(false);
    const [title, setTitle] = useState('');
    // Een mislukte schrijfactie moet te ZIEN zijn: zonder dit lijkt een klik die
    // niets deed op 'opgeslagen'. Niet wegslikken: ook naar de console.
    const [fout, setFout] = useState('');

    /**
     * Voert een schrijfactie uit en maakt een mislukking zichtbaar.
     * @param {() => Promise<void>} actie
     * @param {string} melding - Wat de gebruiker leest als het misgaat
     */
    const schrijf = async (actie, melding) => {
        setFout('');
        try {
            await actie();
        } catch (e) {
            console.error('HouseholdSubtasks:', melding, e);
            setFout(melding);
        }
    };

    // Twee gedeelde listeners, één per soort: de afspraken (met de subtaken van
    // de oude vorm erin) en de subtaken van de nieuwe vorm. De afspraken zijn
    // nodig om het huishouden van een oude subtaak te vinden en om de titel
    // van de afspraak te tonen.
    const visits = useSharedCollectionWhere('Tasks', 'Type', APPOINTMENT_TYPE);
    const newSubtasks = useSharedCollectionWhere('Tasks', 'Type', SUBTASK_TYPE);
    // Pas geladen als ALLEBEI iets gaven: anders zegt de tabel 'nog geen
    // subtaken' terwijl de helft nog onderweg is.
    // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
    const loaded = Array.isArray(visits) && Array.isArray(newSubtasks);

    const subtasks = useMemo(
        () => (loaded ? (subtasksPerAdress([...visits, ...newSubtasks])[adressId] || []) : []),
        [visits, newSubtasks, loaded, adressId],
    );

    // De afspraken van dít huishouden, nieuwste eerst: waar een losse subtaak
    // alsnog aan gehangen kan worden. Alleen echte afspraken, en alleen van dit
    // adres — een keuzelijst met andermans bezoeken erin koppelt werk verkeerd.
    const appointments = useMemo(
        () => (loaded ? visits : [])
            .filter((task) => isAppointment(task) && task.AdressID === adressId)
            .sort((a, b) => (b.Timestamp?.toMillis?.() || 0) - (a.Timestamp?.toMillis?.() || 0)),
        [visits, loaded, adressId],
    );

    // Open werk bovenaan: dat is waar iemand dit tabblad voor opent. Daarbinnen
    // de nieuwste afspraak eerst; nog niet ingeplande subtaken hebben geen datum
    // en komen daardoor ná de ingeplande.
    const sorted = useMemo(() => [...subtasks].sort((a, b) => {
        if (isOpenSubtask(a) !== isOpenSubtask(b)) return isOpenSubtask(a) ? -1 : 1;
        return (b.parentDate?.toMillis?.() || 0) - (a.parentDate?.toMillis?.() || 0);
    }), [subtasks]);

    const columns = [
        { key: 'title', label: 'TAAK' },
        { key: 'status', label: 'STATUS', searchable: false, sortable: true, sortAccessor: (item) => subtaskStatusLabel(item) },
        { key: 'parent', label: 'BIJ AFSPRAAK', searchable: false },
        { key: 'date', label: 'DATUM', searchable: false, sortable: true, sortAccessor: (item) => item.parentDate?.toMillis?.() || 0 },
    ];

    /**
     * Een nieuwe subtaak bij dit huishouden, nog niet ingepland.
     * Zonder titel gebeurt er niets: een 'Naamloze taak' zegt niemand wat.
     * @param {React.FormEvent} e
     */
    const addSubtask = async (e) => {
        e.preventDefault();
        const titel = title.trim();
        if (!titel) return;

        await schrijf(async () => {
            await db.collection('Tasks').add(buildSubtaskDoc({
                id: uuid(),
                client,
                parent: null,
                adressId,
                userId: auth?.ID,
                position: subtasks.length + 1,
                title: titel,
            }));
            setTitle('');
            setAdding(false);
        }, 'De subtaak is niet opgeslagen. Probeer het opnieuw.');
    };

    /**
     * De stand omzetten, langs dezelfde weg als de afspraakbewerker.
     * De waarde eerst uitlezen: `e.target` is het levende element.
     * AI-CONTEXT: See docs/pitfalls.md#state-updater-reads-event-target-too-late
     * @param {object} item
     * @param {React.ChangeEvent<HTMLSelectElement>} e
     */
    const changeStatus = (item, e) => {
        const nieuweStand = e.target.value;
        schrijf(() => runStatusChange(item, nieuweStand), 'De stand is niet opgeslagen. Probeer het opnieuw.');
    };

    /**
     * Een losse subtaak aan een afspraak van dit huishouden hangen: alleen
     * `Parent` verandert, de rest van de subtaak blijft zoals hij is.
     * @param {object} item
     * @param {React.ChangeEvent<HTMLSelectElement>} e
     */
    const linkToAppointment = async (item, e) => {
        const afspraakId = e.target.value;
        if (!afspraakId) return;
        await schrijf(
            () => db.collection('Tasks').doc(item.docid).update({ Parent: afspraakId }),
            'Het koppelen is niet gelukt. Probeer het opnieuw.',
        );
    };

    /**
     * De cel 'BIJ AFSPRAAK'. Bij een niet-ingeplande subtaak komt er, als dit
     * huishouden afspraken heeft, een keuzelijst bij om hem alsnog te koppelen.
     * @param {object} item
     * @returns {JSX.Element}
     */
    const parentCell = (item, canEdit) => (
        <>
            <p>{parentLabel(item)}</p>
            {canEdit && !item.planned && appointments.length > 0 && (
                <select
                    className="household-subtasks__link"
                    aria-label="Koppelen aan afspraak"
                    value=""
                    onChange={(e) => linkToAppointment(item, e)}
                >
                    <option value="">Koppelen aan afspraak…</option>
                    {appointments.map((afspraak) => (
                        <option key={afspraak.ID} value={afspraak.ID}>
                            {`${afspraak.Title || 'Huisbezoek'} · ${formatParentDate(afspraak.Timestamp)}`}
                        </option>
                    ))}
                </select>
            )}
        </>
    );

    return (
        <div style={{ display: adressNavigation === 'subtasks' ? 'block' : 'none' }}>
            <div className="dashboard-container">
                <h2>Subtaken</h2>
                {/* Twee takken op volgorde van zekerheid: 'nog niet bekend' is
                    iets anders dan 'er is niets'. Een lege tabel zou beweren dat
                    er geen werk ligt terwijl de taken nog onderweg zijn.
                    AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading */}
                {!loaded ? (
                    <p className="household-subtasks__empty">Subtaken worden opgehaald…</p>
                ) : (
                    // Zelfde poort als de andere schrijvende tabbladen van het
                    // huishouden ('Afspraken', 'Contactmomenten'): 'Adressen bewerken'.
                    // Zonder recht: kijken mag, het plusje en de keuzelijsten niet.
                    <ReadOnlyGuard edit="addresses.edit">
                    {(canEdit) => (
                    <>
                        {sorted.length > 0 && (
                            <p className="household-subtasks__hint">
                                Hier staat al het werk van dit huishouden bij elkaar. De tekst van een subtaak
                                aanpassen doe je bij de afspraak, op het tabblad <strong>&apos;Afspraken&apos;</strong>.
                            </p>
                        )}
                        <Table
                            columns={columns}
                            items={sorted}
                            getItemKey={(item) => item.ID}
                            showSearch={false}
                            emptyMessage="Nog geen subtaken bij dit huishouden."
                            renderRow={(item) => (
                                <>
                                    <TableCell><p>{item.Title || 'Naamloze taak'}</p></TableCell>
                                    <TableCell>
                                        {canEdit ? (
                                            <select
                                                className="household-subtasks__status"
                                                aria-label={`Stand van ${item.Title || 'naamloze taak'}`}
                                                value={item.Status || 'toDo'}
                                                onChange={(e) => changeStatus(item, e)}
                                            >
                                                {Object.entries(STATUS_LABELS).map(([waarde, label]) => (
                                                    <option key={waarde} value={waarde}>{label}</option>
                                                ))}
                                            </select>
                                        ) : (
                                            <p>{subtaskStatusLabel(item)}</p>
                                        )}
                                    </TableCell>
                                    <TableCell>{parentCell(item, canEdit)}</TableCell>
                                    <TableCell><p>{formatParentDate(item.parentDate)}</p></TableCell>
                                </>
                            )}
                        />
                        {fout && <p className="household-subtasks__error" role="alert">{fout}</p>}
                        {canEdit && (adding ? (
                            <form className="household-subtasks__add" onSubmit={addSubtask}>
                                <label htmlFor="household-subtask-title">Wat moet er gebeuren?</label>
                                <input
                                    id="household-subtask-title"
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    autoFocus
                                />
                                <button type="submit">Toevoegen</button>
                                <button type="button" onClick={() => { setAdding(false); setTitle(''); }}>Annuleren</button>
                            </form>
                        ) : (
                            <AddItemTableRow toolTipContent="Subtaak toevoegen" onClick={() => setAdding(true)} />
                        ))}
                    </>
                    )}
                    </ReadOnlyGuard>
                )}
            </div>
        </div>
    );
};

export { formatParentDate };
export default HouseholdSubtasks;
